import { getActiveSpan, startInactiveSpan, getCurrentScope } from '@sentry/browser';
import { logger, timestampInSeconds } from '@sentry/utils';
import { DEFAULT_HOOKS } from './constants.js';
import { DEBUG_BUILD } from './debug-build.js';
import { formatComponentName } from './vendor/components.js';
var VUE_OP = 'ui.vue'; // Mappings from operation to corresponding lifecycle hook.

var HOOKS = {
  activate: ['activated', 'deactivated'],
  create: ['beforeCreate', 'created'],
  // Vue 3
  unmount: ['beforeUnmount', 'unmounted'],
  // Vue 2
  destroy: ['beforeDestroy', 'destroyed'],
  mount: ['beforeMount', 'mounted'],
  update: ['beforeUpdate', 'updated']
};
/**
 * Grabs active transaction off scope.
 *
 * @deprecated You should not rely on the transaction, but just use `startSpan()` APIs instead.
 */

function getActiveTransaction() {
  // eslint-disable-next-line deprecation/deprecation
  return getCurrentScope().getTransaction();
}
/** Finish top-level span and activity with a debounce configured using `timeout` option */


function finishRootSpan(vm, timestamp, timeout) {
  if (vm.$_sentryRootSpanTimer) {
    clearTimeout(vm.$_sentryRootSpanTimer);
  }

  vm.$_sentryRootSpanTimer = setTimeout(function () {
    if (vm.$root && vm.$root.$_sentryRootSpan) {
      vm.$root.$_sentryRootSpan.end(timestamp);
      vm.$root.$_sentryRootSpan = undefined;
    }
  }, timeout);
}

var createTracingMixins = function createTracingMixins(options) {
  var hooks = (options.hooks || []).concat(DEFAULT_HOOKS) // Removing potential duplicates
  .filter(function (value, index, self) {
    return self.indexOf(value) === index;
  });
  var mixins = {};
  var _iteratorNormalCompletion = true;
  var _didIteratorError = false;
  var _iteratorError = undefined;

  try {
    var _loop = function _loop() {
      var operation = _step.value;
      // Retrieve corresponding hooks from Vue lifecycle.
      // eg. mount => ['beforeMount', 'mounted']
      var internalHooks = HOOKS[operation];

      if (!internalHooks) {
        DEBUG_BUILD && logger.warn("Unknown hook: ".concat(operation));
        return "continue";
      }

      var _iteratorNormalCompletion2 = true;
      var _didIteratorError2 = false;
      var _iteratorError2 = undefined;

      try {
        var _loop2 = function _loop2() {
          var internalHook = _step2.value;

          mixins[internalHook] = function () {
            var isRoot = this.$root === this;

            if (isRoot) {
              var activeSpan = getActiveSpan();

              if (activeSpan) {
                this.$_sentryRootSpan = this.$_sentryRootSpan || startInactiveSpan({
                  name: 'Application Render',
                  op: "".concat(VUE_OP, ".render"),
                  origin: 'auto.ui.vue'
                });
              }
            } // Skip components that we don't want to track to minimize the noise and give a more granular control to the user


            var name = formatComponentName(this, false);
            var shouldTrack = Array.isArray(options.trackComponents) ? options.trackComponents.indexOf(name) > -1 : options.trackComponents; // We always want to track root component

            if (!isRoot && !shouldTrack) {
              return;
            }

            this.$_sentrySpans = this.$_sentrySpans || {}; // Start a new span if current hook is a 'before' hook.
            // Otherwise, retrieve the current span and finish it.

            if (internalHook == internalHooks[0]) {
              var _activeSpan = this.$root && this.$root.$_sentryRootSpan || getActiveSpan();

              if (_activeSpan) {
                // Cancel old span for this hook operation in case it didn't get cleaned up. We're not actually sure if it
                // will ever be the case that cleanup hooks re not called, but we had users report that spans didn't get
                // finished so we finish the span before starting a new one, just to be sure.
                var oldSpan = this.$_sentrySpans[operation];

                if (oldSpan) {
                  oldSpan.end();
                }

                this.$_sentrySpans[operation] = startInactiveSpan({
                  name: "Vue <".concat(name, ">"),
                  op: "".concat(VUE_OP, ".").concat(operation),
                  origin: 'auto.ui.vue'
                });
              }
            } else {
              // The span should already be added via the first handler call (in the 'before' hook)
              var span = this.$_sentrySpans[operation]; // The before hook did not start the tracking span, so the span was not added.
              // This is probably because it happened before there is an active transaction

              if (!span) return;
              span.end();
              finishRootSpan(this, timestampInSeconds(), options.timeout);
            }
          };
        };

        for (var _iterator2 = internalHooks[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
          _loop2();
        }
      } catch (err) {
        _didIteratorError2 = true;
        _iteratorError2 = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion2 && _iterator2["return"] != null) {
            _iterator2["return"]();
          }
        } finally {
          if (_didIteratorError2) {
            throw _iteratorError2;
          }
        }
      }
    };

    for (var _iterator = hooks[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
      var _ret = _loop();

      if (_ret === "continue") continue;
    }
  } catch (err) {
    _didIteratorError = true;
    _iteratorError = err;
  } finally {
    try {
      if (!_iteratorNormalCompletion && _iterator["return"] != null) {
        _iterator["return"]();
      }
    } finally {
      if (_didIteratorError) {
        throw _iteratorError;
      }
    }
  }

  return mixins;
};

export { createTracingMixins, getActiveTransaction };